
// React
import PropTypes from "prop-types";
import { useContext, useMemo, useState } from "react";

// Apollo
import { gql, useQuery } from "@apollo/client";

// UI
import DataTableCustom from "components/Tables/DataTableCustom";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";

// Utils
import dayjs from "dayjs";
import { formatMoneyPunto } from "utils/formatters";
import { UserContext } from "context/user";
import FacturaImpresion from "layouts/pedidos/components/pedidos/FacturaImpresion";
import { Box, Card, Fade, Modal } from "@mui/material";
import Factura from "../Factura";
import SoftBox from "components/SoftBox";
import SoftBadge from "components/SoftBadge";

const GET_PRESUPUESTOS_IDS = gql`
  query getPresupuestosIds($filter: JSON) {
    presupuestoEventos(filter: $filter) {
      id
    }
  }
`;

const GET_VENTAS_EVENTO = gql`
  query getVentasEvento($filter: JSON, $order: Order) {
    ventasEventos(filter: $filter, order: $order) {
      id
      fechaFactura
      tipoFactura
      numeroDeFacturaAfip
      total
      idPresupuestoEvento
      url
      CAE
      CAEFchVto
      concepto
      descuento
      idAnulada
      imp_iva21
      subtotal21
      nro_doc
      nroAnulado
      tipo_cbte
      tipo_doc
      tipoFactura
      pto_vta
      recargo
      evento {
        id
        nombreEvento
      }
      cliente {
        nombre
        apellido
        tipoCliente
        tipoDocumento
        tipoResponsable
        documento
        email
        razonSocial
      }
    }
  }
`;

function FacturaTab({ idEvento }) {
    const [openModalFactura, setOpenModalFactura] = useState(false); // Estado para controlar el modal
    const [facturaData, setFacturaData] = useState(null); // Datos de la factura
    const [empresaData, setEmpresaData] = useState(null); // Datos de la empresa
    const { user } = useContext(UserContext);
    // 1) Obtener los presupuestos del evento para conocer sus IDs
    const {
        loading: loadingPresupuestos,
        data: presupuestosData,
    } = useQuery(GET_PRESUPUESTOS_IDS, {
        variables: { filter: { idEvento } },
        fetchPolicy: "network-only",
        skip: !idEvento,
    });

    const presupuestoIds = useMemo(
        () => (presupuestosData?.presupuestoEventos || []).map((p) => Number(p.id)),
        [presupuestosData]
    );

    const handleOpenFacturaModal = (factura, empresa) => {
        setFacturaData(factura); // Guardar los datos de la factura
        setEmpresaData(empresa); // Guardar los datos de la empresa
        setOpenModalFactura(true); // Abrir el modal
    };

    const handleCloseFacturaModal = () => {
        setOpenModalFactura(false); // Cerrar el modal
        setFacturaData(null); // Limpiar los datos de la factura
        setEmpresaData(null); // Limpiar los datos de la empresa
    };

    const handlePrintFactura = (venta, empresa) => {
        handleOpenFacturaModal(venta, empresa);
    };

    // 2) Traer las ventas asociadas a esos presupuestos, ordenadas por fecha DESC (sin orden local)
    const {
        loading: loadingVentas,
        data: ventasData,
    } = useQuery(GET_VENTAS_EVENTO, {
        variables: {
            filter: { idPresupuestoEvento: presupuestoIds },
            order: { field: "fechaFactura", order: "DESC" },
        },
        fetchPolicy: "network-only",
        skip: !idEvento || !presupuestoIds || presupuestoIds.length === 0,
    });

    const labelEstadoFactura = (cae) => {
        if (cae) return { text: "Emitida", color: "uproGreen" };
        return { text: "Pendiente", color: "uproYellow" };
    };

    const columns = useMemo(
        () => [
            {
                field: "presupuesto",
                headerName: "Presupuesto",
                // width: "14%",
                align: "center",
                sortable: false
            },
            {
                field: "estado",
                headerName: "Estado",
                // width: "14%",
                align: "center",
                sortable: false,
                renderCell: (item) => {
                    return (
                        <SoftBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                            sx={{
                                width: "100%",
                                overflow: "hidden",
                                minWidth: 0,
                            }}
                        >
                            <SoftBadge
                                color={labelEstadoFactura(item?.CAE).color}
                                badgeContent={labelEstadoFactura(item?.CAE).text || "-"}
                            />
                        </SoftBox>
                    );
                }
            },
            {
                field: "fecha",
                headerName: "Fecha",
                // width: "22%",
                align: "center",
                sortable: false
            },
            {
                field: "tipo",
                headerName: "Tipo",
                // width: "12%",
                align: "center",
                sortable: false
            },
            {
                field: "nroAfip",
                headerName: "Nro. AFIP",
                // width: "24%",
                align: "center",
                sortable: false
            },
            {
                field: "total",
                headerName: "Total",
                // width: "18%",
                align: "right",
                sortable: false
            },
        ],
        []
    );

    const rows = useMemo(() => {
        const ventas = ventasData?.ventasEventos || [];
        return ventas.map((v) => ({
            id: v.id,
            fecha: v?.fechaFactura ? dayjs(v.fechaFactura).format("DD/MM/YYYY") : "-",
            tipo: v?.tipoFactura || "-",
            nroAfip: v?.numeroDeFacturaAfip || "-",
            total: `$${formatMoneyPunto(v?.total || 0)}`,
            presupuesto: v?.idPresupuestoEvento || "-",
            url: v?.url || null,
            CAE: v?.CAE || null,
            estado: v?.CAE ? "Emitida" : "Pendiente",
        }));
    }, [ventasData]);

    if (!idEvento) return <SinDatos />;
    if (loadingPresupuestos || loadingVentas) return <Loading />;

    if (!presupuestoIds || presupuestoIds.length === 0) return <SinDatos />;
    if (!rows || rows.length === 0) return <SinDatos />;

    return (
        <>
            <DataTableCustom
                headerStyles={{
                    backgroundColor: "#ffffff",             // fondo blanco
                    borderBottom: "1.5px solid #070606ff",
                    py: 1.1,                               // menos alto
                    "& .MuiTypography-root": {
                        fontWeight: 600,                      // negrita media
                        color: "#344767",                     // gris azulado
                        // textTransform: "none",                // sin uppercase
                        fontSize: "0.7rem",
                        letterSpacing: 0.2,
                    },
                }}
                styleCard={{ border: "none" }}
                columns={columns}
                data={rows}
                loading={loadingVentas}
                showActions={true}
                sortable={false}
                actionsWidth="12%"
                actions={[
                    {
                        icon: "receipt",
                        tooltip: "Factura",
                        label: "Factura",
                        disabled: (item) => !item?.CAE,
                        onClick: (item) => {
                            const venta = ventasData?.ventasEventos.find((v) => v.id === item.id);
                            handlePrintFactura(venta, user.empresa);
                        },
                        variant: "outlined",
                        color: "uproPink",
                        iconOnly: true,
                        circular: true,
                    },
                ]}
                emptyMessage="No hay facturas para mostrar"
                maxHeight="420px"
            />
            <Modal
                open={openModalFactura}
                onClose={handleCloseFacturaModal}
                closeAfterTransition
                aria-labelledby="modal-factura-title"
                aria-describedby="modal-factura-description"
            >
                <Fade in={openModalFactura}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "80%",
                            maxHeight: "90%",
                            overflowY: "auto",
                            bgcolor: "background.paper",
                            boxShadow: 24,
                            p: 4,
                        }}
                    >
                        <Card>
                            {facturaData && empresaData && (
                                <Factura factura={facturaData} empresa={empresaData} />
                            )}
                        </Card>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
}

FacturaTab.propTypes = {
    idEvento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default FacturaTab;
